import { useEffect, useRef } from 'react';

/** History state key marking a learner overlay that should consume browser Back. */
export const LEARNER_MODAL_HISTORY_KEY = '__learnerModal';
export const LEARNER_MODAL_NONCE_KEY = '__learnerModalNonce';

/** LIFO stack of open overlay nonces — only the topmost entry handles Back. */
const modalStack: string[] = [];

let modalHistoryPopConsumers = 0;

/**
 * True when a learner modal just consumed this `popstate` (Back should not change the page view).
 * App navigation handlers should skip view restoration when this returns true.
 */
export function consumeLearnerModalHistoryPop(): boolean {
  if (modalHistoryPopConsumers <= 0) return false;
  modalHistoryPopConsumers -= 1;
  return true;
}

function markLearnerModalHistoryPop(): void {
  modalHistoryPopConsumers += 1;
}

function stripModalKeys(state: Record<string, unknown>): Record<string, unknown> {
  const next = { ...state };
  delete next[LEARNER_MODAL_HISTORY_KEY];
  delete next[LEARNER_MODAL_NONCE_KEY];
  return next;
}

function pushModalHistoryEntry(modalId: string, nonce: string): void {
  const prev = window.history.state;
  const base = typeof prev === 'object' && prev !== null ? { ...prev } : {};
  // Reuse an orphaned modal history slot (e.g. React Strict Mode remount) instead of stacking ghosts.
  if (
    modalStack.length === 0 &&
    typeof prev === 'object' &&
    prev !== null &&
    LEARNER_MODAL_HISTORY_KEY in (prev as object)
  ) {
    modalStack.push(nonce);
    window.history.replaceState(
      { ...stripModalKeys(base), [LEARNER_MODAL_HISTORY_KEY]: modalId, [LEARNER_MODAL_NONCE_KEY]: nonce },
      '',
      window.location.href
    );
    return;
  }
  modalStack.push(nonce);
  window.history.pushState(
    { ...base, [LEARNER_MODAL_HISTORY_KEY]: modalId, [LEARNER_MODAL_NONCE_KEY]: nonce },
    '',
    window.location.href
  );
}

function removeNonceFromStack(nonce: string): void {
  for (let i = modalStack.length - 1; i >= 0; i -= 1) {
    if (modalStack[i] === nonce) {
      modalStack.splice(i, 1);
      break;
    }
  }
}

/**
 * While `active`, browser Back calls `onBack` instead of navigating away.
 * Opening pushes a history entry; UI dismiss strips it via `replaceState` (no async `history.back()` race).
 *
 * Return `true` from `onBack` if the overlay stayed open (e.g. exit confirm);
 * the history entry is re-pushed so another Back can try again.
 */
export function useHistoryBackClose(
  active: boolean,
  onBack: () => boolean | void,
  modalId: string
): void {
  const onBackRef = useRef(onBack);
  onBackRef.current = onBack;
  const generationRef = useRef(0);

  useEffect(() => {
    if (!active) return undefined;

    const generation = ++generationRef.current;
    const nonce = `${modalId}#${generation}`;
    pushModalHistoryEntry(modalId, nonce);

    const onPopState = () => {
      // Stale instance after Strict Mode remount / re-open.
      if (generationRef.current !== generation) return;
      if (modalStack[modalStack.length - 1] !== nonce) return;

      modalStack.pop();
      markLearnerModalHistoryPop();

      const stillOpen = onBackRef.current() === true;
      if (stillOpen) {
        pushModalHistoryEntry(modalId, nonce);
        return;
      }

      // Closed via Back — generation stays so a late cleanup won't strip a newer entry.
      generationRef.current = generation;
    };

    window.addEventListener('popstate', onPopState, true);
    return () => {
      window.removeEventListener('popstate', onPopState, true);
      removeNonceFromStack(nonce);

      // Only the latest generation may touch history. Use replaceState (not history.back)
      // so Strict Mode remount cannot receive a spurious popstate and auto-dismiss.
      if (generationRef.current !== generation) return;
      const state = window.history.state as Record<string, unknown> | null;
      if (state?.[LEARNER_MODAL_NONCE_KEY] === nonce) {
        window.history.replaceState(stripModalKeys(state), '', window.location.href);
      }
    };
  }, [active, modalId]);
}
